<template>
  <div class="flex flex-col items-center py-8 px-4">
    <div class="w-full max-w-lg">
      <div class="mb-6">
        <h2 class="text-base font-semibold text-gray-800">Search Report</h2>
        <p class="text-sm text-gray-500 mt-1">
          Enter a tracking number or search reference to resend a search report. Optionally recreate the report before resending.
        </p>
      </div>

      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <form class="space-y-4" @submit.prevent="onSubmit">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Tracking number / search reference</label>
            <input
              v-model.trim="trackingNumber"
              type="text"
              placeholder="e.g. G260805-0681"
              required
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              :disabled="loading"
            />
          </div>

          <label class="flex items-center gap-2 cursor-pointer select-none">
            <input
              v-model="recreate"
              type="checkbox"
              class="rounded border-gray-300 text-primary focus:ring-primary"
              :disabled="loading"
            />
            <span class="text-sm text-gray-700">Recreate report</span>
          </label>

          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="loading || !trackingNumber">
              <i
                class="fa mr-1.5"
                :class="loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'"
              ></i>
              {{ loading ? "Resending..." : "Resend report" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useToast } from "@/composables";
import { extractErrorMessage, resendSearchReportRequest } from "./recordSearchApi";

const { successToast, errorToast, warningToast } = useToast();

const loading = ref(false);
const trackingNumber = ref("");
const recreate = ref(true);

const onSubmit = async () => {
  const tracking = trackingNumber.value.trim();
  if (!tracking) {
    warningToast("Validation", "Enter a tracking number or search reference");
    return;
  }

  loading.value = true;
  try {
    const response = await resendSearchReportRequest(tracking, recreate.value);
    const message =
      (response?.data as { message?: string } | undefined)?.message ||
      "Search report resend submitted";
    successToast("Success", message);
    trackingNumber.value = "";
    recreate.value = true;
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not resend search report"));
  } finally {
    loading.value = false;
  }
};
</script>
